import FilledButtonM from "@common/components/button/FilledButtonM"
import { PinchableBox } from "@common/components/image/PinchableBox"
import { Menu3Point } from "@common/components/menu/Menu3Point"
import TextM from "@common/components/text/TextM"
import { useApiStatus } from "@common/hooks/useApiStatus"
import { theme } from "@common/theme/theme"
import { DimensionsContext, IS_WEB } from "@common/utils/mobileUtils"
import { apiIdle, apiRequest } from "@data/redux/actions/api"
import {
    activityOfflineModeInvalid,
    activityStartedWithOfflineMode,
    setIsInternetReachable,
    setIsOfflineModeEnabled,
    timerStepStarted,
} from "@data/redux/actions/offlineMode"
import { activitiesService } from "@data/services/activitiesService"
import React, { useContext, useEffect, useRef, useState } from "react"
import {
    Linking,
    Modal,
    Platform,
    Pressable,
    ScrollView,
    View,
} from "react-native"
import CircularProgress from "react-native-circular-progress-indicator/src/circularProgress"
import { ActivityIndicator, IconButton, ProgressBar } from "react-native-paper"
import Animated, {
    Easing,
    useAnimatedStyle,
    useSharedValue,
    withRepeat,
    withSequence,
    withTiming,
} from "react-native-reanimated"
import FontAwesome5 from "react-native-vector-icons/FontAwesome5"
import WebView from "react-native-webview"
import { connect } from "react-redux"
import { checkIsPdf } from "../../../common/components/pdf/pdfHelpers"
import PdfViewer from "../../../common/components/pdf/PdfViewer"
import {
    activityTypesActivitySlugs,
    definedMaterialSlug,
} from "../todo/ActivityPicker"
import { calculateTimerColor } from "./TimerContainer"
import { YoutubePlayer } from "./YoutubePlayer"
import EnonceReader from "./reading/EnonceReader"
import { SamTileClearance } from "../../sam-sessions/components/SamFloatingTile"
import MaterialCorrectionDisplay from "../../../common/components/material/MaterialCorrectionDisplay"

// La feuille de l'énoncé : un cadre blanc à filet, qui la détache de l'écran. Sur le natif, la
// WebView de l'énoncé porte déjà sa marge intérieure — le cadre n'en ajoute pas.
const ENONCE_SHEET_STYLE = {
    backgroundColor: theme.colors.white,
    borderWidth: 1,
    borderColor: theme.colors.lightMore,
    borderRadius: 10,
    overflow: "hidden",
    padding: IS_WEB ? 16 : 0,
    margin: IS_WEB ? 0 : 10,
}

const MaterialDisplayModal = (props) => {
    const {
        handleShowModal,
        material,
        isOpen,
        status = null,
        estimatedDuration = 0,
        time = null,
        noModal = false,
        manageTodo = false,
        field,
        goToNextStep,
        style = {},
        isSplit = false,
        scrollEnabled: scrollEnabledProp,
        annotationOverlay,
        annotationDrawing = false,
    } = props

    const dimensions = useContext(DimensionsContext)
    const samTileClearance = useContext(SamTileClearance)

    const materialType = material?.url
        ? "url"
        : material?.attachments?.length > 0
        ? "image"
        : "text"

    const timerProgress = (time * 100) / estimatedDuration
    const isUrl = materialType === "url"
    const isImage = materialType === "image"
    const isText = materialType === "text"
    const hasTextSubheader =
        !isText && !!material?.text && material?.text !== ""
    const timerColor = calculateTimerColor(timerProgress, status, isImage)
    const isPdfView =
        material?.media_type === "pdf" || checkIsPdf(material?.attachments?.[0])

    const dMaterialSlug = definedMaterialSlug(
        activityTypesActivitySlugs?.[material?.activity_type]
    )

    const opacity = useSharedValue(0)

    // Set the opacity value to animate between 0 and 1
    useEffect(() => {
        opacity.value = withRepeat(
            withSequence(
                withTiming(1, { duration: 500, easing: Easing.ease }),
                withTiming(0, { duration: 500, easing: Easing.ease })
            ),
            -1,
            true
        )
    }, [])
    const customStyle = useAnimatedStyle(
        () => ({
            opacity: opacity.value,
        }),
        []
    )

    // ** URL CASE **

    const webViewRef = useRef()

    const [loadingProgress, setLoadingProgress] = React.useState(0)

    const onLoadingEvent = (syntheticEvent) => {
        // update component to be aware of loading status
        const { nativeEvent } = syntheticEvent
        if (!nativeEvent.loading) {
            setTimeout(() => {
                setLoadingProgress(0)
            }, 300)
        }
    }
    const onLoadProgress = ({ nativeEvent }) => {
        setLoadingProgress(nativeEvent.progress)
    }

    const renderPdfFooter = () => null

    const renderPdfBody = () => {
        return (
            <PdfViewer
                file={material?.attachments?.[0]}
                footer={false}
                noModal={noModal}
                context={{
                    materialId: material?.id,
                    activityType: material?.activity_type,
                }}
            />
        )
    }

    const renderUrlFooter = () => null

    const renderUrlBody = () => {
        // If android and url corresponds to pdf, prepend with https://docs.google.com/gview?embedded=true&url=
        const url =
            Platform.OS === "android" &&
            ((material?.url?.endsWith(".pdf") &&
                material?.url?.startsWith("https")) ||
                (isPdfView && material?.attachments[0]?.startsWith("https")))
                ? "https://docs.google.com/gview?embedded=true&url=" +
                  material?.url
                : material?.url

        return (
            <>
                <View
                    style={{
                        width: "100%",
                        height: 6,
                    }}
                >
                    <ProgressBar
                        progress={loadingProgress}
                        color={theme.colors.primary}
                        style={{
                            width: "100%",
                            height: 6,
                        }}
                    />
                </View>
                {!IS_WEB ? (
                    <WebView
                        style={{ flex: 1 }}
                        ref={webViewRef}
                        javaScriptEnabled={true}
                        allowsFullscreenVideo={true}
                        source={{ uri: url }}
                        onLoadStart={onLoadingEvent}
                        onLoadEnd={onLoadingEvent}
                        onLoadProgress={onLoadProgress}
                    />
                ) : (
                    <View style={{ flex: 1, alignItems: "center" }}>
                        {material?.url?.includes("youtube") ? (
                            <YoutubePlayer url={material?.url} />
                        ) : (
                            <FilledButtonM
                                onPress={() => {
                                    Linking.openURL(material?.url).then()
                                }}
                                style={{ marginTop: 10 }}
                                color={theme.colors.primary}
                                label={"Ouvrir le lien"}
                                icon={"link"}
                            />
                        )}
                    </View>
                )}
            </>
        )
    }

    // ** IMAGE CASE **
    const zoomRef = useRef()
    const [currentImageIndex, setCurrentImageIndex] = useState(0)
    const attachments = material?.attachments || []
    const hasMultipleImages = attachments.length > 1 && !isPdfView

    // Reset to first image when modal opens or attachments change
    React.useEffect(() => {
        if (isOpen && attachments.length > 0) {
            setCurrentImageIndex(0)
        }
    }, [isOpen, attachments.length])

    const handlePreviousImage = () => {
        if (currentImageIndex > 0) {
            // Reset zoom before changing image
            if (zoomRef.current) {
                zoomRef.current.zoomOut()
            }
            setCurrentImageIndex(currentImageIndex - 1)
        }
    }

    const handleNextImage = () => {
        if (currentImageIndex < attachments.length - 1) {
            // Reset zoom before changing image
            if (zoomRef.current) {
                zoomRef.current.zoomOut()
            }
            setCurrentImageIndex(currentImageIndex + 1)
        }
    }

    const renderImageFooter = () => (
        <View
            style={{
                flexDirection: "row",
                width: "100%",
                justifyContent: "center",
            }}
        >
            {IS_WEB && (
                <IconButton
                    size={30}
                    icon={"minus-circle"}
                    iconColor={
                        noModal ? theme.colors.primary : theme.colors.white
                    }
                    style={{}}
                    containerColor={
                        noModal
                            ? theme.colors.whiteTransparent
                            : theme.colors.blackTransparent
                    }
                    onPress={() => {
                        zoomRef.current?.zoomOut()
                    }}
                />
            )}
            <IconButton
                size={30}
                icon={"redo"}
                iconColor={noModal ? theme.colors.primary : theme.colors.white}
                containerColor={
                    noModal
                        ? theme.colors.whiteTransparent
                        : theme.colors.blackTransparent
                }
                onPress={() => {
                    zoomRef.current?.rotate()
                }}
            />
            {IS_WEB && (
                <IconButton
                    size={30}
                    icon={"plus-circle"}
                    iconColor={
                        noModal ? theme.colors.primary : theme.colors.white
                    }
                    containerColor={
                        noModal
                            ? theme.colors.whiteTransparent
                            : theme.colors.blackTransparent
                    }
                    onPress={() => {
                        zoomRef.current?.zoomIn()
                    }}
                />
            )}
            {/* Navigation buttons for multiple images */}
            {hasMultipleImages && (
                <>
                    <IconButton
                        size={30}
                        icon={"chevron-left"}
                        iconColor={
                            noModal ? theme.colors.primary : theme.colors.white
                        }
                        containerColor={
                            noModal
                                ? theme.colors.whiteTransparent
                                : theme.colors.blackTransparent
                        }
                        disabled={currentImageIndex === 0}
                        onPress={handlePreviousImage}
                    />
                    <IconButton
                        size={30}
                        icon={"chevron-right"}
                        iconColor={
                            noModal ? theme.colors.primary : theme.colors.white
                        }
                        containerColor={
                            noModal
                                ? theme.colors.whiteTransparent
                                : theme.colors.blackTransparent
                        }
                        disabled={currentImageIndex === attachments.length - 1}
                        onPress={handleNextImage}
                    />
                </>
            )}
        </View>
    )
    const renderImageBody = () => {
        // Handle both string URIs and objects with uri property
        const getImageUri = (attachment) => {
            if (typeof attachment === "string") {
                return attachment
            }
            return attachment?.uri || attachment
        }

        const currentImageUri =
            attachments.length > 0
                ? getImageUri(attachments[currentImageIndex] || attachments[0])
                : null

        if (!currentImageUri) {
            return null
        }

        return (
            <View style={{ flex: 1 }}>
                {/* Image counter for multiple images */}
                {hasMultipleImages && (
                    <View
                        style={{
                            position: "absolute",
                            top: 10,
                            left: 10,
                            zIndex: 10,
                            backgroundColor: "rgba(0,0,0,0.6)",
                            paddingHorizontal: 12,
                            paddingVertical: 6,
                            borderRadius: 20,
                        }}
                    >
                        <TextM
                            color={theme.colors.white}
                            fontSize={14}
                            fontWeight="Bold"
                        >
                            {currentImageIndex + 1} / {attachments.length}
                        </TextM>
                    </View>
                )}
                <PinchableBox imageUri={currentImageUri} ref={zoomRef} gesturesEnabled={!annotationDrawing}>
                    {annotationOverlay}
                </PinchableBox>
            </View>
        )
    }

    // ** TEXT CASE **
    const renderTextFooter = () => null
    const renderTextBody = () => (
        <ScrollView
            style={{
                backgroundColor: theme.colors.white,
                flex: 1,
                alignSelf: "center",
                width: "100%",
            }}
            contentContainerStyle={[
                !IS_WEB ? { flexGrow: 1 } : { padding: 10 },
                // Dans la séance avec Sam, la fin de l'énoncé (son icône « Aa », la correction)
                // doit pouvoir remonter au-dessus de la tuile qui flotte en bas de l'écran.
                { paddingBottom: (IS_WEB ? 10 : 0) + samTileClearance },
            ]}
            nestedScrollEnabled
            // L'énoncé prend la hauteur de son texte (il ne défile plus de lui-même, comme
            // l'ancienne WebView) : c'est cette vue qui défile, sauf pendant une annotation.
            scrollEnabled={scrollEnabledProp ?? (IS_WEB || !annotationDrawing)}
        >
            <View style={{ position: "relative", flex: 1 }}>
                {annotationOverlay}
                <EnonceReader material={material} sheetStyle={ENONCE_SHEET_STYLE} />

                {/* Correction display with embedded toggle */}
                <MaterialCorrectionDisplay
                    material={material}
                    user={props.user}
                    style={{ margin: 0 }}
                />
            </View>
        </ScrollView>
    )

    const [subheaderTextExpended, setSubheaderTextExpended] = useState(false)
    const onSubheaderTextPress = () => {
        setSubheaderTextExpended(!subheaderTextExpended)
    }
    const subheaderText =
        subheaderTextExpended || material?.text?.length <= (noModal ? 100 : 45)
            ? material?.text
            : material?.text?.substring(0, 45) + "..."
    const subheaderTextColor = !noModal ? theme.colors.white : theme.colors.grey

    useApiStatus(activitiesService.deleteMaterial, null, true, () => {
        handleShowModal()
    })

    const renderContent = () => {
        return (
            <>
                {!noModal && (
                    <View
                        style={{
                            width: "100%",
                            height: 60,
                            paddingRight: 5,
                            flexDirection: "row",
                            alignItems: "center",
                            justifyContent: "space-between",
                        }}
                    >
                        <View
                            style={{
                                flexDirection: "row",
                                alignItems: "center",
                            }}
                        >
                            {!noModal && (
                                <IconButton
                                    icon={"times"}
                                    iconColor={
                                        isImage
                                            ? theme.colors.white
                                            : theme.colors.primary
                                    }
                                    containerColor={theme.colors.transparent}
                                    size={32}
                                    onPress={handleShowModal}
                                />
                            )}
                            {isUrl && (
                                <IconButton
                                    icon={"sync"}
                                    iconColor={
                                        isImage
                                            ? theme.colors.white
                                            : theme.colors.primary
                                    }
                                    containerColor={theme.colors.transparent}
                                    size={24}
                                    onPress={() => {
                                        webViewRef.current?.reload()
                                    }}
                                />
                            )}
                        </View>

                        {estimatedDuration > 0 && (
                            <View
                                style={{
                                    flexDirection: "row",
                                    alignItems: "center",
                                }}
                            >
                                {status !== "finished" &&
                                timerProgress > 100 ? (
                                    <Animated.View
                                        style={[
                                            {
                                                marginRight: 6,
                                            },
                                            customStyle,
                                        ]}
                                    >
                                        <FontAwesome5
                                            name={"exclamation-circle"}
                                            size={26}
                                            color={timerColor}
                                        />
                                    </Animated.View>
                                ) : IS_WEB ? (
                                    <></>
                                ) : (
                                    !noModal && (
                                        <CircularProgress
                                            value={Math.min(timerProgress, 100)}
                                            radius={16}
                                            activeStrokeColor={timerColor}
                                            inActiveStrokeColor={timerColor}
                                            activeStrokeWidth={10}
                                            inActiveStrokeWidth={10}
                                            inActiveStrokeOpacity={0.1}
                                            showProgressValue={false}
                                        />
                                    )
                                )}
                                {props.deleteMaterialRequest ? (
                                    <ActivityIndicator
                                        color={
                                            isImage
                                                ? theme.colors.white
                                                : theme.colors.primary
                                        }
                                    />
                                ) : (
                                    <Menu3Point
                                        menuItems={[
                                            {
                                                title:
                                                    "Supprimer " +
                                                    (dMaterialSlug ??
                                                        "le support"),
                                                onPress: () => {
                                                    props.apiRequest(
                                                        activitiesService.deleteMaterial,
                                                        {
                                                            id: material?.id,
                                                        }
                                                    )
                                                },
                                            },
                                        ]}
                                        color={
                                            isImage
                                                ? theme.colors.white
                                                : theme.colors.primary
                                        }
                                    />
                                )}
                            </View>
                        )}
                    </View>
                )}
                <>
                    {hasTextSubheader && (
                        <Pressable
                            style={{ width: "100%", paddingBottom: 5 }}
                            onPress={onSubheaderTextPress}
                        >
                            <TextM
                                color={subheaderTextColor}
                                style={{ textAlign: "center" }}
                                fontWeight={"Bold"}
                                wrap
                            >
                                {subheaderText}
                            </TextM>
                        </Pressable>
                    )}
                    {isPdfView
                        ? renderPdfBody()
                        : isUrl
                        ? renderUrlBody()
                        : isImage
                        ? renderImageBody()
                        : renderTextBody()}
                    {isPdfView
                        ? renderPdfFooter()
                        : isUrl
                        ? renderUrlFooter()
                        : isImage
                        ? renderImageFooter()
                        : renderTextFooter()}
                    {noModal && (
                        <View
                            style={{
                                flexDirection: "row",
                                alignItems: "center",
                                position: "absolute",
                                top: 10,
                                left: 10,
                                backgroundColor: theme.colors.white,
                                borderRadius: 50,
                            }}
                        >
                            {isUrl && (
                                <IconButton
                                    icon={"sync"}
                                    iconColor={
                                        isImage
                                            ? theme.colors.white
                                            : theme.colors.primary
                                    }
                                    size={24}
                                    onPress={() => {
                                        webViewRef.current?.reload()
                                    }}
                                />
                            )}
                        </View>
                    )}
                    {manageTodo && (
                        <FilledButtonM
                            color={theme.colors.success}
                            onPress={() => {
                                if (
                                    props.materialUrlField.value !== "" ||
                                    props.materialTitleField.value !== "" ||
                                    props.materialAttachmentsField.value
                                        ?.length > 0
                                ) {
                                    props.materialTitleField.onChange("")
                                    props.materialUrlField.onChange("")
                                    props.materialAttachmentsField.onChange([])
                                }
                                field.onChange(material?.id)
                                goToNextStep()
                            }}
                            style={{
                                width: dimensions?.width > 800 ? "50%" : "80%",
                                marginVertical: 10,
                                alignSelf: "center",
                            }}
                            label={
                                ["written_exercise", "oral_exercise"].includes(
                                    material?.activity_type
                                )
                                    ? "Choisir cet énoncé"
                                    : "Choisir ce support"
                            }
                        />
                    )}
                </>
            </>
        )
    }

    if (noModal) {
        return (
            <View
                style={{
                    width: "100%",
                    flex: 1,
                    backgroundColor: theme.colors.white,
                    alignSelf: "center",
                    marginBottom: 20,
                    ...style,
                }}
            >
                {renderContent()}
            </View>
        )
    }

    return (
        <Modal
            visible={isOpen}
            transparent
            animationType="fade"
            onRequestClose={handleShowModal}
        >
            <Pressable
                style={{
                    flex: 1,
                    backgroundColor: "rgba(0,0,0,0.5)",
                    justifyContent: "center",
                    alignItems: "center",
                }}
                onPress={handleShowModal}
            >
                <View
                    style={{
                        width: "96%",
                        maxHeight: "90%",
                        height: "90%",
                        backgroundColor: isImage
                            ? theme.colors.black
                            : theme.colors.white,
                        borderRadius: 10,
                        overflow: "hidden",
                    }}
                >
                    {renderContent()}
                </View>
            </Pressable>
        </Modal>
    )
}

const mapStateToProps = (state) => {
    return {
        deleteMaterialRequest: state.api?.deleteMaterial?.request,
        user: state.data.currentUser?.object,
    }
}

const mapDispatchToProps = {
    activityStartedWithOfflineMode,
    activityOfflineModeInvalid,
    setIsInternetReachable,
    setIsOfflineModeEnabled,
    timerStepStarted,
    apiRequest,
    apiIdle,
}

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(MaterialDisplayModal)
